import {SectionIntro, Section, Box, Grid} from '@primer/react-brand'
import type {FeaturedContentProps} from './types'
import {renderWithNoMarks} from '../../../utils'
import {FlexSuiteRiverAccordion} from '../../../components/FlexSuiteRiverAccordion'
import {FlexSuiteRiverStack} from '../../../components/FlexSuiteRiverStack'
import {FlexSuiteRiverBreakout} from '../../../components/FlexSuiteRiverBreakout'
import {FlexSuiteRiverBreakoutTabs} from '../../../components/FlexSuiteRiverBreakoutTabs'
import {FlexSuiteRiverFootsteps} from '../../../components/FlexSuiteRiverFootsteps'
import {FlexSuiteLargeCards} from '../../../components/FlexSuiteLargeCards'
import {BrandedDivider} from './BrandedDivider'
import {
  FlexSuiteTestimonialQuote,
  FlexSuiteTestimonialStat,
  FlexSuiteTestimonialBanner,
} from '../../../components/FlexSuiteTestimonial'
import overviewStyles from '../../OverviewTemplate.module.css'

const FeaturedContent = ({
  featureHeading,
  featureLabel,
  featureDescription,
  featureLink,
  featureDetailsOne,
  featureRiverAccordion,
  featureRiverStack,
  featureDetailsTwo,
  featureRiverBreakout,
  featureBreakoutTabs,
  featureDetailsThree,
  featureDetailsThreeHeading,
  featureFootstepsLabel,
  featureFootstepsLink,
  featureFootstepsCards,
  featureLargeCards,
  featureDetailsTestimonial,
  featureTestimonialQuote,
  featureTestimonialName,
  featureTestimonialTitle,
  featureTestimonialLink,
  featureTestimonialLabel,
  featureTestimonialLogo,
  featureTestimonialStats,
  featureTestimonialImage,
}: FeaturedContentProps) => {
  return (
    <Section fullWidth paddingBlockStart="condensed">
      <Box
        paddingBlockEnd="spacious"
        paddingBlockStart="spacious"
        className={`${overviewStyles.gridFrameTop}${featureDetailsOne === 'Accordion' ? ` ${overviewStyles.gridFrameBottom}` : ''}`}
      >
        <Grid>
          <Grid.Column>
            <SectionIntro align="center">
              {featureLabel && <SectionIntro.Label>{featureLabel}</SectionIntro.Label>}
              {featureHeading && <SectionIntro.Heading>{renderWithNoMarks(featureHeading)}</SectionIntro.Heading>}
              {featureDescription && (
                <SectionIntro.Description>{renderWithNoMarks(featureDescription)}</SectionIntro.Description>
              )}
              {featureLink?.fields.href && (
                <SectionIntro.Link href={featureLink.fields.href}>{featureLink.fields.text}</SectionIntro.Link>
              )}
            </SectionIntro>
          </Grid.Column>
        </Grid>
      </Box>
      {featureDetailsOne === 'Accordion' && featureRiverAccordion ? (
        <Box
          paddingBlockEnd="spacious"
          paddingBlockStart="spacious"
          className={`${overviewStyles.gridFrameTop} ${overviewStyles.gridFrameBottom}`}
        >
          <Grid>
            <Grid.Column>
              <FlexSuiteRiverAccordion items={featureRiverAccordion} />
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}
      {featureDetailsOne === 'Stack' && featureRiverStack ? (
        <Section>
          <FlexSuiteRiverStack items={featureRiverStack} />
        </Section>
      ) : null}
      {featureDetailsTwo === 'Breakout' && featureRiverBreakout ? (
        <Grid>
          <Grid.Column>
            <FlexSuiteRiverBreakout {...featureRiverBreakout.fields} />
          </Grid.Column>
        </Grid>
      ) : null}
      {featureDetailsTwo === 'Carousel' && featureBreakoutTabs ? (
        <Grid>
          <Grid.Column>
            <FlexSuiteRiverBreakoutTabs items={featureBreakoutTabs} />
          </Grid.Column>
        </Grid>
      ) : null}
      {featureDetailsThree === 'Footsteps' && featureFootstepsCards ? (
        <Box
          className={`${overviewStyles.gridFrameTop} ${overviewStyles.gridFrameBottom}`}
          marginBlockStart={48}
          paddingBlockStart={48}
          paddingBlockEnd={48}
        >
          <FlexSuiteRiverFootsteps
            heading={featureDetailsThreeHeading}
            label={featureFootstepsLabel}
            cards={featureFootstepsCards}
            cta={featureFootstepsLink && featureFootstepsLink.fields}
          />
        </Box>
      ) : null}
      {featureDetailsThree === 'Large Cards' && featureLargeCards ? (
        <Box
          className={`${overviewStyles.gridFrameTop} ${overviewStyles.gridFrameBottom}`}
          marginBlockStart={48}
          paddingBlockStart={48}
          paddingBlockEnd={48}
        >
          <FlexSuiteLargeCards heading={featureDetailsThreeHeading} cards={featureLargeCards} />
        </Box>
      ) : null}
      {featureDetailsTestimonial === 'Quote' && featureTestimonialQuote ? (
        <Box paddingBlockEnd="spacious" paddingBlockStart="spacious">
          <Grid>
            <Grid.Column>
              <FlexSuiteTestimonialQuote
                quote={featureTestimonialQuote}
                name={featureTestimonialName}
                title={featureTestimonialTitle}
                link={featureTestimonialLink && featureTestimonialLink.fields}
                logo={featureTestimonialLogo}
              />
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}
      {featureDetailsTestimonial === 'Statistics' &&
      featureTestimonialStats &&
      featureTestimonialQuote &&
      featureTestimonialLink &&
      featureTestimonialImage ? (
        <Box marginBlockStart={48}>
          <FlexSuiteTestimonialStat
            quote={featureTestimonialQuote}
            name={featureTestimonialName}
            title={featureTestimonialTitle}
            link={featureTestimonialLink.fields}
            label={featureTestimonialLabel}
            image={featureTestimonialImage}
            stats={featureTestimonialStats}
          />
        </Box>
      ) : null}
      {featureDetailsTestimonial === 'Brand Banner' && featureTestimonialQuote ? (
        <Box paddingBlockEnd="spacious" paddingBlockStart="spacious">
          <Grid>
            <Grid.Column>
              <FlexSuiteTestimonialBanner
                quote={featureTestimonialQuote}
                link={featureTestimonialLink && featureTestimonialLink.fields}
                logo={featureTestimonialLogo}
                image={featureTestimonialImage}
              />
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}
      {/* Final Branded Divider will be added in https://github.com/github/marketing-engineering/issues/5476 */}
      <Box className={overviewStyles.gridFrameBottom} paddingBlockStart="normal">
        <Grid>
          <Grid.Column>
            <BrandedDivider />
          </Grid.Column>
        </Grid>
      </Box>
    </Section>
  )
}
export {FeaturedContent}
